/**
 * Arithmetic Primitive Operations
 *
 * Fundamental mathematical operations for numeric computations.
 */

import { defineOperation, PrimitiveOperation } from './operations.model';

// ------------------------------------------------
// BINARY ARITHMETIC OPERATIONS
// ------------------------------------------------

export const add = defineOperation<number, number>({
  id: 'op.add',
  name: 'Addition',
  symbol: '+',
  category: 'arithmetic',
  arity: 2,
  execute: (a, b) => a + b,
  latex: '{{a}} + {{b}}',
  description: 'Adds two numbers together',
  example: '$3 + 4 = 7$',
});

export const sub = defineOperation<number, number>({
  id: 'op.sub',
  name: 'Subtraction',
  symbol: '−',
  category: 'arithmetic',
  arity: 2,
  execute: (a, b) => a - b,
  latex: '{{a}} - {{b}}',
  description: 'Subtracts the second number from the first',
  example: '$7 - 3 = 4$',
});

export const mul = defineOperation<number, number>({
  id: 'op.mul',
  name: 'Multiplication',
  symbol: '×',
  category: 'arithmetic',
  arity: 2,
  execute: (a, b) => a * b,
  latex: '{{a}} \\times {{b}}',
  description: 'Multiplies two numbers',
  example: '$3 \\times 4 = 12$',
});

export const div = defineOperation<number, number>({
  id: 'op.div',
  name: 'Division',
  symbol: '÷',
  category: 'arithmetic',
  arity: 2,
  execute: (a, b) => a / b,
  latex: '\\frac{{{a}}}{{{b}}}',
  description: 'Divides the first number by the second',
  example: '$\\frac{12}{4} = 3$',
});

export const pow = defineOperation<number, number>({
  id: 'op.pow',
  name: 'Exponentiation',
  symbol: '^',
  category: 'arithmetic',
  arity: 2,
  execute: (base, exponent) => Math.pow(base, exponent),
  latex: '{{a}}^{{{b}}}',
  description: 'Raises the base to the power of the exponent',
  example: '$2^3 = 8$',
});

export const mod = defineOperation<number, number>({
  id: 'op.mod',
  name: 'Modulo',
  symbol: 'mod',
  category: 'arithmetic',
  arity: 2,
  execute: (a, b) => a % b,
  latex: '{{a}} \\mod {{b}}',
  description: 'Returns the remainder after division',
  example: '$17 \\mod 5 = 2$',
});

// ------------------------------------------------
// UNARY ARITHMETIC OPERATIONS
// ------------------------------------------------

export const neg = defineOperation<number, number>({
  id: 'op.neg',
  name: 'Negation',
  symbol: '−',
  category: 'arithmetic',
  arity: 1,
  execute: (a) => -a,
  latex: '-{{a}}',
  description: 'Negates a number (flips its sign)',
  example: '$-(5) = -5$',
});

export const abs = defineOperation<number, number>({
  id: 'op.abs',
  name: 'Absolute Value',
  symbol: '|x|',
  category: 'arithmetic',
  arity: 1,
  execute: (a) => Math.abs(a),
  latex: '|{{a}}|',
  description: 'Returns the non-negative value of a number',
  example: '$|-7| = 7$',
});

export const sqrt = defineOperation<number, number>({
  id: 'op.sqrt',
  name: 'Square Root',
  symbol: '√',
  category: 'arithmetic',
  arity: 1,
  execute: (a) => Math.sqrt(a),
  latex: '\\sqrt{{{a}}}',
  description: 'Returns the square root of a number',
  example: '$\\sqrt{16} = 4$',
});

export const floor = defineOperation<number, number>({
  id: 'op.floor',
  name: 'Floor',
  symbol: '⌊x⌋',
  category: 'arithmetic',
  arity: 1,
  execute: (a) => Math.floor(a),
  latex: '\\lfloor {{a}} \\rfloor',
  description: 'Rounds down to the nearest integer',
  example: '$\\lfloor 3.7 \\rfloor = 3$',
});

export const ceil = defineOperation<number, number>({
  id: 'op.ceil',
  name: 'Ceiling',
  symbol: '⌈x⌉',
  category: 'arithmetic',
  arity: 1,
  execute: (a) => Math.ceil(a),
  latex: '\\lceil {{a}} \\rceil',
  description: 'Rounds up to the nearest integer',
  example: '$\\lceil 3.2 \\rceil = 4$',
});

export const round = defineOperation<number, number>({
  id: 'op.round',
  name: 'Round',
  symbol: 'round',
  category: 'arithmetic',
  arity: 1,
  execute: (a) => Math.round(a),
  latex: '\\text{round}({{a}})',
  description: 'Rounds to the nearest integer',
  example: '$\\text{round}(3.5) = 4$',
});

// ------------------------------------------------
// EXPORT ALL ARITHMETIC OPERATIONS
// ------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const arithmeticOperations: Array<PrimitiveOperation<any, any>> = [
  add,
  sub,
  mul,
  div,
  pow,
  mod,
  neg,
  abs,
  sqrt,
  floor,
  ceil,
  round,
];
